import { z } from 'zod';
import {
  AuditSchema,
  ClockTimeSchema,
  PartyIdSchema,
  TenantScopedSchema,
  TimeWindowSchema,
  UserIdSchema,
  WeekdaySchema,
} from './primitives.js';
import { LocaleSchema } from './tenancy.js';

/* ------------------------------------------------------------------ *
 * One Party, many roles.
 *
 * The brief lists Customers, Suppliers, Ambassadors, Businesses, Users
 * as separate things. Modelling them as separate tables is the trap:
 * the farm you buy tomatoes from also buys your olive oil, the
 * ambassador who brings you restaurants is also a customer, and the
 * hotel group is one legal entity with six delivery addresses.
 *
 * So: a Party is a legal or physical entity. Roles are flags on it.
 * Adding "carrier" or "franchisee" later is one enum member.
 * ------------------------------------------------------------------ */

export const PartyRoleSchema = z.enum([
  'customer',
  'supplier',
  'ambassador', // introduces customers, earns commission
  'carrier',    // third-party delivery
  'partner',    // another Prody business you trade with
]);
export type PartyRole = z.infer<typeof PartyRoleSchema>;

export const PartyKindSchema = z.enum(['company', 'person']);
export type PartyKind = z.infer<typeof PartyKindSchema>;

/* --- Channels: one contact point, many transports ----------------- */

export const ChannelKindSchema = z.enum([
  'whatsapp',
  'telegram',
  'email',
  'sms',
  'phone',
  'signal',
  'instagram',
]);
export type ChannelKind = z.infer<typeof ChannelKindSchema>;

export const ContactChannelSchema = z.object({
  kind: ChannelKindSchema,
  /** Phone in E.164, email address, @handle — normalised per kind on write. */
  address: z.string().min(1),
  label: z.string().nullable().default(null),
  /** Where replies go by default when this party is messaged. */
  primary: z.boolean().default(false),
  verifiedAt: z.string().datetime({ offset: true }).nullable().default(null),
  /** Opt-out is per channel, not per party. GDPR and WhatsApp policy both require it. */
  optedOut: z.boolean().default(false),
});
export type ContactChannel = z.infer<typeof ContactChannelSchema>;

/* --- People inside a company -------------------------------------- */

export const ContactPersonSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /** Free text on purpose: "chef", "jefe de compras", "night receiving". */
  role: z.string().nullable().default(null),
  channels: z.array(ContactChannelSchema).default([]),
  /** The person who actually approves the order. Drives who gets pinged. */
  isDecisionMaker: z.boolean().default(false),
  notes: z.string().nullable().default(null),
});
export type ContactPerson = z.infer<typeof ContactPersonSchema>;

/* --- Addresses and delivery ---------------------------------------- */

export const AddressSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  line1: z.string().min(1),
  line2: z.string().nullable().default(null),
  city: z.string().min(1),
  postcode: z.string().min(1),
  region: z.string().nullable().default(null),
  country: z.string().length(2),
  lat: z.number().nullable().default(null),
  lng: z.number().nullable().default(null),
  /** "Ring the bell at the side door, kitchen is downstairs." Drivers read this, not the address. */
  accessNotes: z.string().nullable().default(null),
});
export type Address = z.infer<typeof AddressSchema>;

/**
 * When this party can receive goods. A restaurant that only accepts
 * deliveries 07:00–11:00 is not an edge case, it is the norm, and
 * routing is worthless without it.
 */
export const DeliveryPreferenceSchema = z.object({
  addressId: z.string().min(1),
  weekdays: z.array(WeekdaySchema).min(1),
  window: TimeWindowSchema,
  /** Hard windows fail routing; soft windows only warn. */
  strict: z.boolean().default(false),
});
export type DeliveryPreference = z.infer<typeof DeliveryPreferenceSchema>;

/* --- Commercial terms ---------------------------------------------- */

export const PaymentTermsSchema = z.object({
  /** Net days from invoice date. 0 = on delivery. */
  netDays: z.number().int().min(0).max(180).default(0),
  /** Credit ceiling in minor units of the business currency. null = unlimited. */
  creditLimitMinor: z.number().int().nullable().default(null),
  /** Block new orders once the limit is passed, instead of only warning. */
  blockOnOverLimit: z.boolean().default(false),
  method: z.enum(['cash', 'transfer', 'card', 'direct-debit', 'other']).default('transfer'),
});
export type PaymentTerms = z.infer<typeof PaymentTermsSchema>;

/* --- The party ------------------------------------------------------ */

export const PartySchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: PartyIdSchema,
  kind: PartyKindSchema,
  /** A party can be several things at once. This is the whole point. */
  roles: z.array(PartyRoleSchema).min(1),
  /** Short human code used on labels and in the command palette: "SORLI", "CAN-PUIG". */
  code: z.string().min(1),
  name: z.string().min(1),
  legalName: z.string().nullable().default(null),
  taxId: z.string().nullable().default(null),
  /** What language this party's documents and messages go out in. */
  locale: LocaleSchema.nullable().default(null),

  channels: z.array(ContactChannelSchema).default([]),
  people: z.array(ContactPersonSchema).default([]),
  addresses: z.array(AddressSchema).default([]),
  deliveryPreferences: z.array(DeliveryPreferenceSchema).default([]),

  paymentTerms: PaymentTermsSchema,
  /** Price list applied before personal prices. */
  priceListId: z.string().nullable().default(null),
  /** Sales rep / buyer who owns the relationship. */
  ownerId: UserIdSchema.nullable().default(null),
  /** Who introduced this customer, for commission. */
  referredByPartyId: PartyIdSchema.nullable().default(null),

  /* Supplier-side fields. Present on every party, meaningful only when
     'supplier' is in roles — the alternative is a second table and a
     join on every screen that shows "who do I owe money to". */
  supplier: z
    .object({
      /** 'market' = a stand at Mercabarna, 'farm' = direct, needs a run day. */
      sourceKind: z.enum(['market', 'farm', 'importer', 'manufacturer', 'other']),
      /** Which days a buyer physically goes there. Empty = delivers to us. */
      runDays: z.array(WeekdaySchema).default([]),
      /** Stand or gate number on the market floor. Buyers navigate by these. */
      standCode: z.string().nullable().default(null),
      /** Latest time we can still place a order for next-day pickup. */
      orderCutoff: ClockTimeSchema.nullable().default(null),
      leadTimeDays: z.number().int().min(0).default(0),
    })
    .nullable()
    .default(null),

  /* Ambassador-side. */
  ambassador: z
    .object({
      commissionBps: z.number().int().min(0).max(10_000),
      /** Commission stops after this many months per referred customer. null = forever. */
      durationMonths: z.number().int().positive().nullable().default(null),
    })
    .nullable()
    .default(null),

  tags: z.array(z.string()).default([]),
  notes: z.string().nullable().default(null),
  status: z.enum(['active', 'lead', 'paused', 'blocked']).default('active'),
});
export type Party = z.infer<typeof PartySchema>;

export const hasRole = (party: Party, role: PartyRole): boolean => party.roles.includes(role);

export const primaryChannel = (
  party: Party,
  kind?: ChannelKind,
): ContactChannel | undefined => {
  const pool = kind ? party.channels.filter((c) => c.kind === kind) : party.channels;
  return pool.find((c) => c.primary && !c.optedOut) ?? pool.find((c) => !c.optedOut);
};
